/**
 * 워크북을 막 불러왔을 때 DSD 워크북 파일명 아래에 띄우는 1회 안내
 * (Figma 7246:577434). 연결·별도 각각 한 번씩만 뜨고, 화면 클릭 한 번이나
 * 파일 정보 줄이 스크롤로 다 접히면 닫힌다.
 *
 * 예산(유형별 1회)을 당기는 조건과 안내가 실제로 렌더되는 조건은 **같아야 한다**.
 * 안내는 DSD 워크북 칸에 붙고 그 칸은 `DSD워크북 보기`를 켠 동안에만 있어서,
 * 보기를 끈 채로 유형을 바꾸면 예산만 쓰이고 안내는 영영 못 보는 길이 생긴다.
 * 그래서 `showWorkbook` 도 함께 본다.
 */

import { useEffect, useRef, useState } from "react";

import type { FsType } from "@/apis/ixd-editor/types";

interface UseWorkbookLoadedHintInput {
  /** 워크북이 붙어 비교 중인지 — 안내는 이때부터 의미가 있다 */
  isComparing: boolean;
  /** `DSD워크북 보기` — 꺼져 있으면 안내가 붙을 칸 자체가 없다 */
  showWorkbook: boolean;
  fsType: FsType;
  /** 파일 정보 줄이 스크롤로 다 접혔는지 — 가리키던 파일명이 화면에서 사라진다 */
  rowHidden: boolean;
}

/** 지금 이 유형의 안내를 띄울 차례인지 */
export const useWorkbookLoadedHint = ({
  isComparing,
  showWorkbook,
  fsType,
  rowHidden,
}: UseWorkbookLoadedHintInput) => {
  const shownFsTypesRef = useRef(new Set<FsType>());
  const [hintFsType, setHintFsType] = useState<FsType | null>(null);
  useEffect(() => {
    if (!isComparing || !showWorkbook) return;
    if (shownFsTypesRef.current.has(fsType)) return;
    shownFsTypesRef.current.add(fsType);
    setHintFsType(fsType);
  }, [fsType, isComparing, showWorkbook]);

  // 클릭 한 번이면 닫는다. capture 로 듣기만 하고 이벤트는 그대로 흘려보낸다
  // — 안내를 닫으려고 누른 클릭이 값 반영·목차 이동 같은 원래 동작을 잃지 않게 한다.
  useEffect(() => {
    if (!hintFsType) return;
    const close = () => setHintFsType(null);
    document.addEventListener("click", close, { capture: true });
    return () =>
      document.removeEventListener("click", close, { capture: true });
  }, [hintFsType]);

  // 줄이 다 접혀 파일명이 사라지면 닫는다 — 다시 펼쳐도 돌아오지 않는다(1회 안내).
  useEffect(() => {
    if (rowHidden) setHintFsType(null);
  }, [rowHidden]);

  return hintFsType === fsType;
};
